import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Download, Send, FileText } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth, authHeaders } from "../../lib/auth";
import StatusTimeline from "./StatusTimeline";
import StatusPill from "./StatusPill";

export default function Workspace() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/dashboard/orders/${orderId}`, { headers: authHeaders(token) });
      setOrder(r.data);
      setNotes(r.data.customer_notes || "");
    } catch { setError(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId, token]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/dashboard/orders/${orderId}/uploads`, fd, {
        headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeFile = async (fileId) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/dashboard/orders/${orderId}/uploads/${fileId}`, { headers: authHeaders(token) });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/dashboard/orders/${orderId}/notes`, { notes }, { headers: authHeaders(token) });
      toast.success("Notes saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/dashboard/orders/${orderId}/submit`, {}, { headers: authHeaders(token) });
      toast.success("Submitted to coach.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReview = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/dashboard/orders/${orderId}/review`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = order?.review_file?.original_name || "brep-review";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't download review."));
  };

  if (error) return <div className="p-12"><p className="text-white/50">Order not found.</p><Link to="/dashboard" className="text-[#2A5BFF]">← Back</Link></div>;
  if (!order) return <div className="p-12 text-white/40" data-testid="workspace-loading">Loading…</div>;

  const status = order.workspace_status || "awaiting_upload";
  const files = order.customer_files || [];
  const locked = ["submitted", "in_review", "completed"].includes(status);
  const completed = status === "completed";

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-5xl" data-testid={`workspace-${order.id}`}>
      <button onClick={() => nav("/dashboard")} className="text-[11px] tracking-[0.28em] uppercase text-white/50 hover:text-white inline-flex items-center gap-2" data-testid="workspace-back">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{new Date(order.created_at).toLocaleDateString()}</div>
          <h1 className="font-anton uppercase text-4xl md:text-5xl leading-[0.95] mt-1" data-testid="workspace-title">{order.service_title}</h1>
          <div className="mt-3 flex flex-wrap gap-3 text-[10px] tracking-[0.24em] uppercase text-white/60">
            <span>Payment confirmed · {(order.currency || "").toUpperCase()} {Number(order.amount).toFixed(2)}</span>
            <span>Coach · {order.coach_name || "Assigning…"}</span>
            <span>ETA · {order.estimated_turnaround}</span>
          </div>
        </div>
        <StatusPill status={status} testid="workspace-status" />
      </div>

      <div className="mt-10">
        <StatusTimeline status={status} />
      </div>

      {/* Completed → Download review */}
      {completed && (
        <section className="mt-10 border border-[#2A5BFF]/60 bg-[#2A5BFF]/[0.05] p-6 md:p-8" data-testid="workspace-completed">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#2A5BFF]">Review complete</div>
          <h2 className="font-anton uppercase text-3xl mt-2">Your review is ready.</h2>
          {order.completed_at && (
            <p className="mt-2 text-white/60 text-sm" data-testid="workspace-completed-date">
              Completed on {new Date(order.completed_at).toLocaleDateString()}
            </p>
          )}
          <button onClick={downloadReview} className="btn-primary mt-6 inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="workspace-download-review">
            <Download size={14} /> Download Review
          </button>
        </section>
      )}

      {/* Uploads */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-anton uppercase text-2xl">Your files</h2>
          {!locked && (
            <>
              <input ref={fileInput} type="file" className="hidden" onChange={onFile} data-testid="workspace-file-input" />
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
                data-testid="workspace-upload-button"
              >
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </>
          )}
        </div>
        <div className="border border-white/10 divide-y divide-white/10">
          {files.length === 0 && <div className="p-5 text-white/40 text-sm" data-testid="workspace-no-files">No files yet.</div>}
          {files.map((f) => (
            <div key={f.id} className="p-4 flex items-center justify-between gap-4" data-testid={`workspace-file-${f.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={16} className="text-[#2A5BFF]" />
                <div className="min-w-0">
                  <div className="truncate text-white/90 text-sm">{f.original_name}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">{(f.size / 1024).toFixed(1)} KB · {new Date(f.uploaded_at).toLocaleString()}</div>
                </div>
              </div>
              {!locked && (
                <button onClick={() => removeFile(f.id)} aria-label="Delete file" className="w-9 h-9 border border-white/15 hover:border-red-500 hover:text-red-500 flex items-center justify-center transition-colors" data-testid={`workspace-file-delete-${f.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Notes */}
      <section className="mt-10">
        <h2 className="font-anton uppercase text-2xl mb-4">Notes for your coach</h2>
        <textarea
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={locked}
          placeholder="Anything the coach should know — goals, injuries, camera angles, etc."
          className="input-line focus-ring resize-none w-full"
          data-testid="workspace-notes"
        />
        {!locked && (
          <button onClick={saveNotes} className="btn-ghost mt-4 px-4 py-2 text-[11px] tracking-[0.24em] uppercase" data-testid="workspace-save-notes">Save notes</button>
        )}
      </section>

      {/* Submit */}
      {!locked && (
        <section className="mt-10 border-t border-white/10 pt-8 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-white/60 text-sm max-w-xl">Ready when you are. Once you submit, files and notes lock and the coach begins your review.</div>
          <button onClick={submit} disabled={submitting || files.length === 0} className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60" data-testid="workspace-submit">
            <Send size={14} /> {submitting ? "Submitting…" : "Submit to coach"}
          </button>
        </section>
      )}
    </div>
  );
}
